<script setup lang="ts">
import toRem from "@/helpers/shared/toRem";
import { computed, toRefs } from "vue";

interface Props {
  src?: string;
  alt: string;
  grayscale?: boolean;
  /**
   * REM
   */
  size?: number;
}

const props = withDefaults(defineProps<Props>(), {
  size: 5,
});

const { alt, size } = toRefs(props);

const getFirstChar = (value?: string) => {
  if (!value) {
    return "";
  }

  return value.charAt(0);
};

const initials = computed(() => {
  const parts = alt.value.split(" ");
  const first = parts.at(0);
  const last = parts.at(-1);

  return `${getFirstChar(first)}${getFirstChar(last)}`.trim().toUpperCase();
});

const currentFont = computed(() => {
  if (size.value <= 1.5) return "text-xs";
  if (size.value <= 2) return "text-sm";
  if (size.value <= 3) return "text-base";
  if (size.value <= 4) return "text-lg";
  if (size.value <= 5) return "text-xl";
  if (size.value <= 6) return "text-2xl";
  return "text-3xl";
});
</script>

<template>
  <div class="relative h-fit">
    <div
      class="bg-cover bg-center flex-shrink-0 rounded-full overflow-hidden ring-2 ring-white bg-neutral200 stroke-white stroke-2 shadow-lg"
      :style="{
        height: toRem(size),
        width: toRem(size),
        minHeight: toRem(size),
        minWidth: toRem(size),
      }"
    >
      <img
        v-if="src"
        :src="src"
        :alt="alt"
        loading="lazy"
        class="w-full h-full object-cover rounded-full"
        :class="{ grayscale }"
      />
      <div
        v-else
        class="b400 w-full h-full flex justify-center items-center text-neutral600 text-md"
        :class="currentFont"
      >
        {{ initials }}
      </div>
    </div>

    <slot v-if="$slots.icon" name="icon"></slot>
  </div>
</template>
